/**
 * Plan tier helpers for desktop publish gates (mirrors apps/web/lib/plans.ts).
 * Roles (writer / author / editor) align with billing plans.
 */

export type UserPlan = "free" | "author" | "editor";

const PLAN_ORDER: UserPlan[] = ["free", "author", "editor"];

export function parseUserPlan(value: unknown): UserPlan {
  if (value === "author" || value === "editor" || value === "free") return value;
  if (value === "publisher") return "editor";
  return "free";
}

export function isPlanAtLeast(userPlan: UserPlan, required: UserPlan): boolean {
  return PLAN_ORDER.indexOf(userPlan) >= PLAN_ORDER.indexOf(required);
}

/** Minimum plan for each capability. */
export const FEATURE_MIN_PLAN = {
  /** Basic manuscript DOCX / simple PDF / EPUB */
  publishExport: "author" as UserPlan,
  /**
   * Full ZIP package / premium themes / cover wrap / store preflight.
   * Actual check uses canPublishPackage (Editor or Publish Pro), not Author alone.
   */
  publishPackage: "author" as UserPlan,
  /** Beta reads */
  betaRead: "author" as UserPlan,
  /** AI features (Story Craft, chat, TTS, covers) — managed cloud requires Author+ */
  aiFeatures: "author" as UserPlan,
  /** Editor client roster / import client books */
  editorRoster: "editor" as UserPlan,
} as const;

export type PlanGateResult =
  | { ok: true }
  | { ok: false; upgrade: true; error: string };

/** Premium package / cover wrap / store ZIP — Editor includes; otherwise Publish Pro. */
export function canPublishPackage(
  plan: UserPlan | unknown,
  hasPublishPro = false
): boolean {
  const userPlan = parseUserPlan(plan);
  if (userPlan === "editor") return true;
  return Boolean(hasPublishPro);
}

export function gatePlanFeature(
  plan: UserPlan | unknown,
  feature: keyof typeof FEATURE_MIN_PLAN,
  /** When true and plan unknown/offline, allow (fail open). */
  failOpenWhenUnknown = true,
  options?: { hasPublishPro?: boolean }
): PlanGateResult {
  if (plan == null || plan === "") {
    return failOpenWhenUnknown
      ? { ok: true }
      : {
          ok: false,
          upgrade: true,
          error: "Sign in to verify your plan before continuing.",
        };
  }
  const userPlan = parseUserPlan(plan);
  if (feature === "publishPackage") {
    if (canPublishPackage(userPlan, Boolean(options?.hasPublishPro))) return { ok: true };
    return {
      ok: false,
      upgrade: true,
      error: "Publish package requires the Publish Pro add-on (included with Editor).",
    };
  }
  const required = FEATURE_MIN_PLAN[feature];
  if (isPlanAtLeast(userPlan, required)) return { ok: true };
  const label =
    feature === "editorRoster"
      ? "Editor tools"
      : feature === "betaRead"
        ? "Beta reads"
        : feature === "aiFeatures"
          ? "AI features"
          : "Publish export";
  const need = required === "editor" ? "an Editor plan" : "an Author or Editor plan";
  return {
    ok: false,
    upgrade: true,
    error: `${label} requires ${need}.`,
  };
}
